import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Calendar,
  CreditCard,
  Building,
  AlertTriangle,
  Sparkles,
  TrendingUp,
  Info,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/lib/supabase";
import { formatCurrency } from "@/lib/commission";
import { formatCPF, formatCNPJ, formatPIXKey } from "@/lib/formatters";
import { MIN_WITHDRAWAL_AMOUNT, WITHDRAWAL_OPEN_DAY } from "@/lib/constants";

interface WithdrawalRequest {
  id: string;
  user_id: string;
  amount: number;
  pix_key: string;
  cnpj: string;
  status: "pending" | "completed" | "rejected";
  created_at: string;
  updated_at?: string;
}

interface WithdrawalFormData {
  pix_key: string;
  cnpj: string;
}

export const WithdrawalRequest: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [availableAmount, setAvailableAmount] = useState(0);
  const [formData, setFormData] = useState<WithdrawalFormData>({
    pix_key: "",
    cnpj: "",
  });
  const [taxIdType, setTaxIdType] = useState<"cpf" | "cnpj">("cnpj");

  const MIN_WITHDRAWAL = MIN_WITHDRAWAL_AMOUNT;
  const isWithdrawalWindowOpen = new Date().getDate() >= WITHDRAWAL_OPEN_DAY;

  // Handle Tax ID (CPF/CNPJ) input change with formatting
  const handleTaxIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = taxIdType === "cpf" ? formatCPF(value) : formatCNPJ(value);
    setFormData({ ...formData, cnpj: formatted });
  };

  // Handle PIX key input change with formatting
  const handlePIXKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numbers = value.replace(/\D/g, "");

    // Limit to 14 digits for numeric PIX keys
    if (numbers.length > 14) {
      return; // Don't update if more than 14 digits
    }

    let formatted = value;
    if (numbers.length === 11 || numbers.length === 14) {
      formatted = formatPIXKey(value);
    }

    setFormData({ ...formData, pix_key: formatted });
  };

  const fetchWithdrawalRequests = useCallback(async () => {
    if (!user) return;

    try {
      const response = await apiGet(`/api/withdrawals?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      } else {
        console.error("Error fetching withdrawal requests");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  type MonthlyBalance = { commission_amount: number | null };
  type WithdrawalRow = { amount: number | null };

  const calculateAvailableAmount = useCallback(async () => {
    if (!user) return;

    try {
      const { data: balances, error: balancesError } = await supabase
        .from("monthly_commission_balances")
        .select("commission_amount")
        .eq("user_id", user.id);

      if (balancesError) throw balancesError;

      const commissionsTotal = ((balances || []) as MonthlyBalance[]).reduce(
        (sum, b) => sum + Number(b.commission_amount || 0),
        0
      );

      const { data: withdrawals, error: withdrawalsError } = await supabase
        .from("withdrawal_requests")
        .select("amount")
        .eq("user_id", user.id)
        .in("status", ["pending", "completed"]);

      if (withdrawalsError) throw withdrawalsError;

      const withdrawnTotal = ((withdrawals || []) as WithdrawalRow[]).reduce(
        (sum, w) => sum + Number(w.amount || 0),
        0
      );

      setAvailableAmount(Math.max(0, commissionsTotal - withdrawnTotal));
    } catch (error) {
      console.error("Error calculating available amount:", error);
      setAvailableAmount(0);
    }
  }, [user]);

  useEffect(() => {
    fetchWithdrawalRequests();
    calculateAvailableAmount();
  }, [fetchWithdrawalRequests, calculateAvailableAmount]);

  const cancelRequest = async (requestId: string) => {
    if (!user) return;

    setCancelling(requestId);
    try {
      // Optimistically restore available if cancelling a pending request
      const reqToCancel = requests.find((r) => r.id === requestId);

      const response = await apiPost(`/api/withdrawals/cancel`, {
        request_id: requestId,
        user_id: user.id,
      });

      if (response.ok) {
        // If it was pending, immediately reflect back into available balance
        if (reqToCancel && reqToCancel.status === "pending") {
          setAvailableAmount((prev) => prev + Number(reqToCancel.amount || 0));
        }
        // Remove the cancelled request from the list
        setRequests(requests.filter((req) => req.id !== requestId));
        toast.success("Solicitação cancelada com sucesso!");
        // Reconcile in background
        setTimeout(() => {
          calculateAvailableAmount();
        }, 500);
      } else {
        const error = await response.json();
        toast.error(error.error || "Erro ao cancelar solicitação");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erro ao cancelar solicitação");
    } finally {
      setCancelling(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.pix_key.trim() || !formData.cnpj.trim()) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }

    // Use net available directly (already accounts for pending/paid in calculation)
    const visibleAvailableAmount = Math.max(availableAmount, 0);

    if (visibleAvailableAmount <= MIN_WITHDRAWAL) {
      toast.error("Você precisa ter mais de R$ 100,00 disponível para solicitar saque");
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiPost("/api/withdrawals", {
        user_id: user.id,
        pix_key: formData.pix_key.trim(),
        cnpj: formData.cnpj.trim(),
      });

      if (response.ok) {
        const newRequest = await response.json();
        // Optimistically decrease available by the created amount (server returns full available amount)
        setAvailableAmount((prev) => Math.max(0, prev - Number(newRequest.amount || 0)));
        setRequests([newRequest, ...requests]);
        setFormData({ pix_key: "", cnpj: "" });
        setIsDialogOpen(false);
        toast.success("Solicitação de saque criada com sucesso!");
        // Reconcile in background
        setTimeout(() => {
          calculateAvailableAmount();
        }, 500);
      } else {
        const error = await response.json();
        toast.error(error.error || "Erro ao criar solicitação de saque");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erro ao criar solicitação de saque");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Pendente
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="secondary" className="bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Pago
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="secondary" className="bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Rejeitado
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-muted-foreground">Carregando solicitações de saque...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Withdrawal schedule info */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Como funcionam os saques</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Solicite seu saque entre os dias <span className="font-semibold">{WITHDRAWAL_OPEN_DAY}</span> e o <span className="font-semibold">último dia do mês</span></li>
                <li>Os pagamentos são analisados e realizados <span className="font-semibold">até o dia 5</span> do mês seguinte via PIX</li>
                <li>Valor mínimo para saque: <span className="font-semibold">{formatCurrency(MIN_WITHDRAWAL)}</span></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        {(() => {
          const pendingTotal = requests.filter((r) => r.status === "pending").reduce((s, r) => s + (r.amount || 0), 0);
          const paidTotal = requests.filter((r) => r.status === "completed").reduce((s, r) => s + (r.amount || 0), 0);
          const lastPayment = requests.filter((r) => r.status === "completed").sort((a, b) => 
            new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()
          )[0];
          const visibleAvailableAmount = Math.max(availableAmount, 0);
          const meetsMinimum = visibleAvailableAmount > MIN_WITHDRAWAL;
          const isEligible = meetsMinimum && isWithdrawalWindowOpen;
          const progressPercentage = Math.min((visibleAvailableAmount / MIN_WITHDRAWAL) * 100, 100);
          
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Available Amount Card - Enhanced with gradient and glow */}
              <Card className="relative overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent pointer-events-none" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-green-600 animate-pulse" />
                    Disponível para Saque
                  </CardTitle>
                  <CardDescription>Saldo acumulado das suas vendas</CardDescription>
                </CardHeader>
                <CardContent className="relative space-y-4">
                  <div className="text-4xl font-bold text-green-600 animate-in fade-in duration-500">
                    {formatCurrency(visibleAvailableAmount)}
                  </div>
                  
                  {/* Progress indicator */}
                  {!isEligible && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Progresso para saque mínimo</span>
                        <span className="font-medium">{Math.round(progressPercentage)}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 dark:from-green-400 dark:to-green-500 transition-all duration-500 ease-out"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Faltam {formatCurrency(MIN_WITHDRAWAL - visibleAvailableAmount)} para solicitar
                      </p>
                    </div>
                  )}
                  
                  <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <DialogTrigger asChild>
                            <Button 
                              disabled={!isEligible} 
                              size="lg"
                              className="w-full relative overflow-hidden group shadow-md hover:shadow-lg transition-all duration-300"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                              <Plus className="w-5 h-5 mr-2 relative z-10" />
                              <span className="relative z-10 font-semibold">Solicitar Saque</span>
                            </Button>
                          </DialogTrigger>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          {!isWithdrawalWindowOpen
                            ? `Saques disponíveis a partir do dia ${WITHDRAWAL_OPEN_DAY} de cada mês`
                            : isEligible
                              ? "Pagamentos processados via PIX"
                              : `Mínimo necessário: ${formatCurrency(MIN_WITHDRAWAL)}`}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Solicitar Saque</DialogTitle>
                      </DialogHeader>

                      {/* Disclaimer */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-amber-800">
                            <p className="font-medium mb-1">⚠️ Atenção!</p>
                            <p>
                              Certifique-se de inserir as informações corretas. Dados incorretos podem causar atrasos no
                              processamento do pagamento.
                            </p>
                          </div>
                        </div>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="pix_key">Chave PIX</Label>
                          <div className="relative">
                            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                              id="pix_key"
                              type="text"
                              placeholder="Digite sua chave PIX"
                              value={formData.pix_key}
                              onChange={handlePIXKeyChange}
                              className="pl-10"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Documento</Label>
                          <div className="flex items-center gap-4 text-sm">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="taxIdType"
                                value="cpf"
                                checked={taxIdType === "cpf"}
                                onChange={() => {
                                  setTaxIdType("cpf");
                                  const digits = formData.cnpj.replace(/\D/g, "");
                                  setFormData({ ...formData, cnpj: digits ? formatCPF(digits) : "" });
                                }}
                              />
                              CPF
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="taxIdType"
                                value="cnpj"
                                checked={taxIdType === "cnpj"}
                                onChange={() => {
                                  setTaxIdType("cnpj");
                                  const digits = formData.cnpj.replace(/\D/g, "");
                                  setFormData({ ...formData, cnpj: digits ? formatCNPJ(digits) : "" });
                                }}
                              />
                              CNPJ
                            </label>
                          </div>
                          <Label htmlFor="tax_id" className="mt-2">
                            {taxIdType === "cpf" ? "CPF" : "CNPJ"}
                          </Label>
                          <div className="relative">
                            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                              id="tax_id"
                              type="text"
                              placeholder={taxIdType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                              value={formData.cnpj}
                              onChange={handleTaxIdChange}
                              className="pl-10"
                              required
                            />
                          </div>
                        </div>
                        <div className="bg-muted p-4 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-2">Resumo da solicitação:</p>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Valor solicitado:</span>
                              <span className="font-medium">{formatCurrency(visibleAvailableAmount)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsDialogOpen(false)}
                            className="flex-1"
                          >
                            Cancelar
                          </Button>
                          <Button type="submit" disabled={submitting} className="flex-1">
                            {submitting ? "Enviando..." : "Confirmar Saque"}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                  {!isWithdrawalWindowOpen && meetsMinimum && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">
                          Saques disponíveis apenas de <span className="font-semibold">{WITHDRAWAL_OPEN_DAY} a {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()}</span> de cada mês.
                          Os pagamentos são analisados e realizados até o dia 5.
                        </p>
                      </div>
                    </div>
                  )}
                  {!meetsMinimum && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Mínimo para saque: {formatCurrency(MIN_WITHDRAWAL)}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Pending Balance Card */}
              <Card className="relative overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-yellow-600" />
                    Em Análise
                  </CardTitle>
                  <CardDescription>Aguardando processamento</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-600">{formatCurrency(pendingTotal)}</div>
                  {pendingTotal > 0 && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Processamento em andamento
                    </p>
                  )}
                </CardContent>
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-transparent pointer-events-none" />
              </Card>

              {/* Paid Balance Card */}
              <Card className="relative overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Total Recebido
                  </CardTitle>
                  <CardDescription>
                    {lastPayment 
                      ? `Último: ${format(new Date(lastPayment.updated_at || lastPayment.created_at), "dd/MM/yy", { locale: ptBR })}` 
                      : "Nenhum pagamento ainda"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatCurrency(paidTotal)}</div>
                </CardContent>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent pointer-events-none" />
              </Card>
            </div>
          );
        })()}

        {/* Withdrawal Requests List */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Histórico de Solicitações
            </CardTitle>
            <CardDescription>Acompanhe o status das suas solicitações</CardDescription>
          </CardHeader>
          <CardContent>
            {requests.length > 0 ? (
              <div className="space-y-3">
                {requests.map((request, index) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 hover:shadow-sm transition-all duration-200 animate-in fade-in slide-in-from-bottom-2"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-semibold text-lg">{formatCurrency(request.amount)}</p>
                        {getStatusBadge(request.status)}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p className="flex items-center gap-2">
                          <CreditCard className="h-3 w-3" />
                          PIX: {request.pix_key}
                        </p>
                        <p className="flex items-center gap-2">
                          <Building className="h-3 w-3" />
                          {request.cnpj}
                        </p>
                        <p className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          Solicitado em: {formatDate(request.created_at)}
                        </p>
                        {request.updated_at && request.status !== "pending" && (
                          <p className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Processado em: {formatDate(request.updated_at)}
                          </p>
                        )}
                      </div>
                    </div>
                    {request.status === "pending" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => cancelRequest(request.id)}
                        disabled={cancelling === request.id}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 border-red-200"
                      >
                        {cancelling === request.id ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2" />
                            Cancelando...
                          </>
                        ) : (
                          "Cancelar"
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 bg-primary/5 rounded-full blur-2xl" />
                  </div>
                  <DollarSign className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4 relative" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Nenhuma solicitação ainda</h3>
                <p className="text-muted-foreground max-w-md mx-auto mb-4">
                  Quando você acumular comissões das suas vendas, poderá solicitar seu saque aqui. 
                  Compartilhe seu cupom para começar a vender!
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Sparkles className="h-4 w-4" />
                  <span>Mínimo para saque: {formatCurrency(MIN_WITHDRAWAL)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};